// What signed-out iPads sent (gap #18, office-review prototype, F-57): commands that arrived after
// the office signed an iPad out, or that the server refused as done on it, wait here unapplied
// (sync.quarantine.list). An owner or manager applies one, once, as the technician who sent it, or
// discards it (sync.quarantine.resolve); a refusal stays open with the server's words. Review queue
// only, as the prototype has it.
// BL-189: a Section over a RowList, its Show a Segmented. A row is what the iPad sent, who sent it from
// which iPad and when; Apply and Discard… are its two actions at rest, and Discard… asks first in the same two places
// (Discard it, Keep it waiting). Rows, not a table, so it fits the aside without scrolling.

import { useEffect, useState } from 'react';
import { dateText, dateTimeText } from '@flowology/kernel';
import { RpcError } from '@flowology/client';
import { RowList, Segmented, type Row } from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';

type Status = 'open' | 'applied' | 'discarded';
export interface QuarantineItem {
  id: string;
  staffName: string | null;
  deviceLabel: string | null;
  deviceRevokedAt: string | null;
  deviceRevokedReason: string | null;
  deviceWipedAt: string | null;
  deviceLastSeenAt: string | null;
  action: string;
  title: string;
  jobNumber: string | null;
  occurredAt: string | null;
  receivedAt: string;
  status: Status;
}

/** "Sam's iPad · signed out Oct 15 · wiped Oct 16" (or "not wiped yet, last seen …"). */
export function deviceText(q: QuarantineItem, timeZone: string): string {
  const day = (at: string) => dateText(at, timeZone, { year: false });
  return [
    q.deviceLabel ?? 'An iPad',
    q.deviceRevokedAt ? `signed out ${day(q.deviceRevokedAt)}` : null,
    q.deviceWipedAt
      ? `wiped ${day(q.deviceWipedAt)}`
      : q.deviceRevokedAt
        ? `not wiped yet${q.deviceLastSeenAt ? `, last seen ${day(q.deviceLastSeenAt)}` : ''}`
        : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const SHOW: [Status, string][] = [
  ['open', 'Waiting'],
  ['applied', 'Applied'],
  ['discarded', 'Discarded'],
];

export function QuarantineSection({ tick, timeZone }: { tick: number; timeZone: string }) {
  const [show, setShow] = useState<Status>('open');
  const [items, setItems] = useState<QuarantineItem[] | null>(null);
  const [again, setAgain] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [why, setWhy] = useState<Record<string, string>>({});
  const [discarding, setDiscarding] = useState<string | null>(null);
  useEffect(() => {
    void client
      .call<{ items: QuarantineItem[] }>('sync.quarantine.list', { status: show })
      .then((r) => setItems(r.items))
      .catch(() => setItems([]));
  }, [show, tick, again]);
  // Nothing has ever waited and nothing waits now: the section stays out of the way.
  if (items === null || (show === 'open' && items.length === 0)) return null;
  const resolve = async (q: QuarantineItem, decision: 'apply' | 'discard') => {
    setBusy(q.id);
    try {
      await client.call('sync.quarantine.resolve', { id: q.id, decision });
      setWhy(({ [q.id]: _, ...rest }) => rest);
      setDiscarding(null);
      setAgain((n) => n + 1);
    } catch (e) {
      setWhy((w) => ({ ...w, [q.id]: e instanceof RpcError ? e.message : 'That did not work. Try again.' }));
    } finally {
      setBusy(null);
    }
  };
  const open = show === 'open';
  const at = (when: string) => dateTimeText(when, timeZone, { year: false });
  const row = (q: QuarantineItem): Row => ({
    id: q.id,
    // what it sent in words, the action's key on hover
    name: <span title={q.action}>{q.title}</span>,
    lines: [
      { text: `${q.staffName ?? 'A technician'} · ${deviceText(q, timeZone)}` },
      {
        text: [
          q.occurredAt ? `Done on the iPad ${at(q.occurredAt)}` : null,
          `${q.occurredAt ? 'arrived' : 'Arrived'} ${at(q.receivedAt)}`,
        ]
          .filter(Boolean)
          .join(' · '),
      },
      // the server's words, when it couldn't apply it
      ...(why[q.id]
        ? [
            {
              part: (
                <p className="rv__bad" role="alert">
                  {why[q.id]}
                </p>
              ),
            },
          ]
        : []),
    ],
    ...(q.jobNumber ? { end: { aside: `#${q.jobNumber}` } } : {}),
    ...(open
      ? {
          atRest: 2 as const,
          actions:
            discarding === q.id
              ? [
                  {
                    id: 'discard',
                    text: busy === q.id ? 'Discarding…' : 'Discard it',
                    isDisabled: busy !== null,
                    onPress: () => void resolve(q, 'discard'),
                  },
                  {
                    id: 'keep',
                    text: 'Keep it waiting',
                    isDisabled: busy !== null,
                    onPress: () => setDiscarding(null),
                  },
                ]
              : [
                  {
                    id: 'apply',
                    text: busy === q.id ? 'Applying…' : 'Apply',
                    isDisabled: busy !== null,
                    onPress: () => void resolve(q, 'apply'),
                  },
                  {
                    id: 'ask',
                    text: 'Discard…',
                    isDisabled: busy !== null,
                    onPress: () => setDiscarding(q.id),
                  },
                ],
        }
      : {}),
  });
  return (
    <Section title="Sent from signed-out iPads" {...(open ? { count: items.length } : {})}>
      <div className="rv-sent">
        <p className="rv__muted">
          These came from an iPad after it was signed out, or the server couldn’t apply them as done, so
          nothing happened yet. Apply one (it runs once, as the technician who sent it) or discard it.
        </p>
        <div className="rv-sent__show">
          <Segmented
            label="Show"
            items={SHOW.map(([id, label]) => ({ id, label }))}
            selectedKey={show}
            onSelectionChange={(id) => setShow(id as Status)}
          />
        </div>
        <RowList label="What signed-out iPads sent" rows={items.map(row)} empty="Nothing here." />
      </div>
    </Section>
  );
}
